<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Employee Management</span>
        <h1 class="hris-page-title">Employees</h1>
        <p class="hris-page-subtitle">Manage employee records, imports, exports, and career updates.</p>
      </div>

      <div class="hris-toolbar">
        <q-btn unelevated class="hris-q-primary" label="Export Employee" @click="downloadModal"
          v-show="canExport"></q-btn>
        <q-btn-dropdown unelevated class="hris-q-outline" label="Import" v-show="canImport">
          <q-list>
            <q-item clickable v-close-popup @click="importEmployees">
              <q-item-section>
                <q-item-label>Import Employees</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="importAccount">
              <q-item-section>
                <q-item-label>Import Bank Details</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="importGovernment">
              <q-item-section>
                <q-item-label>Import Government Number</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup @click="importBasicSalary">
              <q-item-section>
                <q-item-label>Import Basic Salary</q-item-label>
              </q-item-section>
            </q-item>

          </q-list>
        </q-btn-dropdown>

        <q-btn-dropdown unelevated class="hris-q-outline" label="Template" v-show="canDownload">
          <q-list>
            <q-item clickable v-close-popup
              @click="downloadTemplate('uploads/template/employeestemplate.csv', 'EmployeeTemplate')">
              <q-item-section>
                <q-item-label>Employee Template</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup
              @click="downloadTemplate('uploads/template/account_template.csv', 'BankAccountTemplate')">
              <q-item-section>
                <q-item-label>Bank Details Template</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup
              @click="downloadTemplate('uploads/template/goverment_template.csv', 'GovernmentTemplate')">
              <q-item-section>
                <q-item-label>Government Number Template</q-item-label>
              </q-item-section>
            </q-item>

            <q-item clickable v-close-popup
              @click="downloadTemplate('uploads/template/basicsalary_template.csv', 'BasicsalaryTemplate')">
              <q-item-section>
                <q-item-label>Basic Salary Template </q-item-label>
              </q-item-section>
            </q-item>

          </q-list>
        </q-btn-dropdown>

        <q-select v-model="branch" :options="branchOptions" label="Branch" option-value="value"
          option-label="label" emit-value map-options @input="getEmployees" outlined dense color="green"
          style="width: 180px" tabindex="0"></q-select>
        <q-select v-model="department" :options="departments" option-value="id" option-label="name" emit-value
          map-options label="Department" @input="getEmployees" outlined dense color="green"
          style="width: 200px"></q-select>

        <div class="hris-toolbar-spacer"></div>
        <q-input dense outlined color="green" v-model="filter" placeholder="Search Employee"
          style="min-width: 220px;"></q-input>
      </div>

      <div class="hris-panel">
        <q-table class="my-sticky-column-table" flat :dense="$q.screen.lt.md" :grid="$q.screen.xs" :data="employees"
          :columns="columns" row-key="id" separator="cell" :pagination.sync="pagination" :loading="loading"
          :filter="filter" @request="onRequest" binary-state-sort>
          <template v-slot:body="props">
            <q-tr slot="body" :props="props">
              <q-td key="photo" :props="props">
                <q-avatar>
                  <img :src="apiUrl + props.row.image" onerror="../../assets/boy-avatar.png">
                </q-avatar>
              </q-td>
              <q-td key="companyname" :props="props">{{ props.row.companyname }}</q-td>
              <q-td key="location" :props="props">{{ props.row.branch }}</q-td>
              <q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
              <q-td key="firstname" :props="props">{{ props.row.firstname }}</q-td>
              <q-td key="middlename" :props="props">{{ props.row.middlename }}</q-td>
              <q-td key="lastname" :props="props">{{ props.row.lastname }}</q-td>
              <q-td key="position" :props="props">{{ props.row.position }}</q-td>
              <q-td key="account" :props="props">{{ props.row.account }}</q-td>
              <q-td key="payroll_group" :props="props">
                <span v-if="props.row.payroll_group" class="hris-badge hris-badge-accent">{{ props.row.payroll_group }}</span>
              </q-td>
              <q-td key="is_active" :props="props">
                <span class="hris-badge"
                  :class="props.row.is_active == 'active' ? 'hris-badge-success' : props.row.is_active == 'hold' ? 'hris-badge-warning' : 'hris-badge-danger'">
                  {{ props.row.is_active }}
                </span>
              </q-td>

              <q-td key="action" :props="props">
                <div>
                  <q-btn flat round dense class="hris-q-ghost" @click="previewRow(props)">
                    <i class="fas fa-eye"></i>
                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Preview 201 File</strong>
                    </q-tooltip>
                  </q-btn>
                  <q-btn flat round dense class="hris-q-ghost" @click="selectedRow(props)" v-show="canEdit">
                    <i class="fas fa-user-edit"></i>
                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Edit 201 File</strong>
                    </q-tooltip>
                  </q-btn>
                  <q-btn flat round dense class="hris-q-ghost" @click="careerUpdate(props)" v-show="canUpdateCareer">
                    <i class="fas fa-chart-line"></i>
                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Update Career</strong>
                    </q-tooltip>
                  </q-btn>
                  <q-btn flat round dense class="hris-q-ghost" @click="MBFUpdate(props)" v-show="canMBF">
                    <i class="fas fa-address-card"></i>
                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Gov. Mandatory Number</strong>
                    </q-tooltip>
                  </q-btn>
                  <q-btn flat round dense class="hris-q-ghost" @click="meritsUpdate(props)" v-show="canMerits">
                    <i class="fas fa-award"></i>
                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Merits/Demerits</strong>
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-td>
            </q-tr>
          </template>
          <template v-slot:no-data="">
            <div class="hris-empty full-width">No employee records match your filters.</div>
          </template>
          <template v-slot:loading>
            <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
              <q-spinner color="green" size="40px"></q-spinner>
              <div>Loading employees...</div>
            </div>
          </template>
        </q-table>
      </div>

      <q-dialog v-model="spinner" persistent>
        <div class="hris-loading" style="background: var(--hris-surface); border-radius: var(--hris-radius); padding: 32px 40px;">
          <q-spinner color="green" size="3em"></q-spinner>
          <div>Processing...</div>
        </div>
      </q-dialog>

      <q-dialog v-model="importAccountDialog" persistent transition-show="scale" transition-hide="scale">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Select Bank Template File</h2>
            <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost"></q-btn>
          </div>
          <q-form ref="form" autocomplete="off" @submit="onSubmit">
            <div class="hris-modal-body">
              <input type="file" id="file" ref="file"
                :rules="[val => val && val.length > 0 || 'This field is required!']" v-on:change="handleFileUpload()"
                required />
            </div>
            <div class="hris-modal-footer">
              <q-btn flat label="Close" class="hris-q-ghost" @click="closeModal"></q-btn>
              <q-btn unelevated label="Submit" type="submit" class="hris-q-primary"></q-btn>
            </div>
          </q-form>
        </div>
      </q-dialog>

      <EditDialog></EditDialog>
      <ImportDialog></ImportDialog>
      <ChangeStatus></ChangeStatus>
      <CareerUpdate></CareerUpdate>
      <FilterExport></FilterExport>
      <MBFDialog></MBFDialog>
      <MeritsDialog></MeritsDialog>
      <BasicSalaryDialog></BasicSalaryDialog>
      <ImportGonvernmentMandatory></ImportGonvernmentMandatory>
      <PreviewDialog></PreviewDialog>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import mixins from '../../mixins';
import EditDialog from './EditModal';
import ImportDialog from './ImportModal';
import ChangeStatus from './ChangeStatusModal';
import CareerUpdate from './CareerUpdate';
import FilterExport from './FilterExportDialog';
import MBFDialog from './MBFDialog';
import MeritsDialog from './MeritsDemerits';
import BasicSalaryDialog from './BasicSalaryDialog';
import ImportGonvernmentMandatory from './ImportGonvernmentMandatory';
import PreviewDialog from './PreviewModal';
export default {
  name: 'ListEmployee',
  components: {
    "EditDialog": EditDialog,
    "ImportDialog": ImportDialog,
    "ChangeStatus": ChangeStatus,
    "CareerUpdate": CareerUpdate,
    "FilterExport": FilterExport,
    "MBFDialog": MBFDialog,
    "MeritsDialog": MeritsDialog,
    "BasicSalaryDialog": BasicSalaryDialog,
    "ImportGonvernmentMandatory": ImportGonvernmentMandatory,
    "PreviewDialog": PreviewDialog,
  },
  data() {
    return {
      filter: '',
      loading: false,
      pagination: {
        page: 1,
        rowsPerPage: 15,
        rowsNumber: 10
      },

      canDownload: false,
      canImport: false,
      canExport: false,
      canEdit: false,
      canUpdateCareer: false,
      canMBF: false,
      canMerits: false,

      canDownloadValue: 1,
      canImportValue: 2,
      canEmportValue: 3,
      canEditValue: 4,
      canUpdateCareerValue: 5,
      canMBFValue: 6,
      canMeritsValue: 7,

      exportModaL: false,
      importfile: false,
      template: null,
      status: 'ALL',
      department: 0,
      branch: 'ALL',
      spinner: false,

      columns: [
        { name: 'photo', align: 'left', label: 'Photo', field: '', sortable: true },
        { name: 'companyname', align: 'center', label: 'Company', field: 'companyname', sortable: true },
        { name: 'location', align: 'center', label: 'Site Location', field: 'location', sortable: true },
        { name: 'employee_id', align: 'center', label: 'Employee ID', field: 'employee_id', sortable: true },
        { name: 'firstname', align: 'center', label: 'First Name', field: 'firstname', sortable: true },
        { name: 'middlename', align: 'center', label: 'Middle Name', field: 'middlename', sortable: true },
        { name: 'lastname', align: 'center', label: 'Last Name', field: 'lastname', sortable: true },
        { name: 'position', align: 'center', label: 'Position', field: 'position', sortable: true },
        { name: 'account', align: 'center', label: 'Account', field: 'account', sortable: true },
        { name: 'payroll_group', align: 'center', label: 'Payroll Group', field: 'payroll_group', sortable: true },
        { name: 'is_active', align: 'center', label: 'Status', field: 'is_active', sortable: true },
        { name: 'action', align: 'center', label: 'Action', field: '', sortable: true },
        ],
        branchOptions: [],
        employees: [],
        statuses: [],
        departments: [],
        //ruben::29/08/2023
        importAccountDialog: false,
        apiUrl: process.env.VUE_APP_API_URL,
        }

        },

        created() {
        this.getDropdownProperties()
        this.getEmployees()
        this.autoHoldEmployee()
        this.autoCareerUpdate()
        this.eventHub.$on('refreshEmployeeList', this.getEmployees);
        this.getMyPermission()

        },


        methods: {
        // ================================================
        // ruben::29/08/2023 start
        // ================================================

        importAccount() {
        this.importAccountDialog = true
        },

        onSubmit: function () {
        this.spinner = true
        let formData = new FormData();
        formData.append("files", this.file);
        axios({
        // onUploadProgress: uploadEvent => {
        //   console.log("Progress: " + Math.round(uploadEvent.loaded / uploadEvent.total * 100) + '%')
        // },
        headers: { 'Authorization': `Bearer ${this.token}` },
        url: this.apiUrl + '/employee/import-account',
        data: formData,
        method: 'POST'
        }).then(response => {

        setTimeout(() => {
        this.spinner = false
        this.importfile = false
        }, 500)
        // this.eventHub.$emit('refreshEmployeeList');
        this.$q.notify({
        message: 'Successful',
        color: 'green',
        position: 'center',
        actions: [
        { label: 'Dismiss', color: 'white', handler: () => { /* ... */ } }
        ]
        })
        }).catch((error) => {
        var message = error.response.data.exception[1].message
        var index = message.indexOf("1062");
        var final = message.substring(index + 4);
        this.$q.notify({
        message: final,
        color: 'negative',
        position: 'center',
        actions: [
        { label: 'Dismiss', color: 'white', handler: () => { /* ... */ } }
        ]
        })
        this.spinner = false
        })
        },

        handleFileUpload() {
        this.file = this.$refs.file.files[0];
        },

        closeModal: function () {
        this.importAccountDialog = false
        },
        // ==============================================================
        // ruben::29/08/2023 end
        // ==============================================================

        //start if server side
        onRequest(props) {

        const { page, rowsPerPage } = props.pagination


        const filter = props.filter
        this.loading = true
        setTimeout(() => {
        this.getRowsNumberCount(filter).then(total => {
        this.pagination.rowsNumber = total
        })
        const fetchCount = rowsPerPage === 0 ? this.pagination.rowsNumber : rowsPerPage
        const startRow = (page - 1) * rowsPerPage
        this.fetchFromServer(startRow, fetchCount, filter)
        this.pagination.page = page
        this.pagination.rowsPerPage = rowsPerPage
        }, 1500)
        },

        fetchFromServer(startRow, count, filter) {
        axios.get(this.apiUrl + '/employees', {
        headers: { 'Authorization': `Bearer ${this.token}` },
        params: {
        status: 'ACTIVE',
        branch: this.branch,
        department: this.department,
        roleID: this.userData.role_id,
        start: startRow,
        countPerPage: count,
        filter: filter,
        }
        }).then((response) => {
        this.employees = response.data.data
        this.loading = false
        }).catch((error) => {
        console.log(error);
        })
        },

        async getRowsNumberCount(filter) {
        let response = await axios.get(this.apiUrl + '/employees/total', {
        headers: { 'Authorization': `Bearer ${this.token}` },
        params: {
        status: 'ACTIVE',
        branch: this.branch,
        department: this.department,
        roleID: this.userData.role_id,
        filter: filter,
        }
        })
        return response.data
        },

        ///end of server side

        getEmployees: function () {
        this.onRequest({ pagination: this.pagination, filter: '' })
        },

        previewRow(emp) {
        this.eventHub.$emit('PreviewEmployee', { id: emp.row.id });
        },

        selectedRow(emp) {
        this.eventHub.$emit('EditEmployee', { stat: true, employee: emp });
        },

        careerUpdate(emp) {
        this.eventHub.$emit('CareerUpdates', { stat: true, employee: emp });
        },

        MBFUpdate(emp) {
        this.eventHub.$emit('MBFUpdate', { stat: true, employee: emp });
        },

        meritsUpdate(emp) {
        this.eventHub.$emit('meritsUpdate', { stat: true, employee: emp });
        },


        downloadTemplate: function (url, filename) {
        axios({
        url: this.apiUrl + '/' + url,
        headers: {
        'Authorization': `Bearer ${this.token}`
        },
        method: 'GET',
        responseType: 'arraybuffer',
        }).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(fileLink);
        fileLink.click();
        });
        },

        importEmployees: function () {
        this.eventHub.$emit('importFileDialog', { status: true });
        },

        importGovernment: function () {
        this.eventHub.$emit('importGovDialog', { status: true });
        },

        importBasicSalary: function () {
        this.eventHub.$emit('bsDialog', { status: true });
        },

        changeStatus: function (status, obj) {
        this.eventHub.$emit('changeEmpStatus', { status: status, emp: obj });
        },

        getDropdownProperties: function () {
        axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
        }).get('/employee/dropdown/properties')
        .then((res) => {
        this.paymentmode = res.data.data.paymentMode
        this.payingbank = res.data.data.bank
        this.payclass = res.data.data.payClass
        this.gender = res.data.data.gender
        const branchList = res.data.data.branches || []
        this.branchOptions = branchList.map((branch) => ({
        label: branch.name,
        value: branch.id
        }))
        this.branchOptions.unshift({ label: 'ALL', value: 'ALL' })
        this.statuses = res.data.data.status
        this.statuses.unshift('ALL')
        this.departments = res.data.data.department
        this.departments.unshift({ 'id': 0, 'name': 'ALL' })
        });
        },

        autoCareerUpdate: function () {
        axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
        }).get('/employee/check/careerupdate')
        .then((res) => {
        console.log(res.data.msg)
        });
        },

        autoHoldEmployee: function () {
        axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
        }).get('/employee/hold/status')
        .then((res) => {
        console.log(res.data.message)
        });
        },

        autoActivateEmployee: function () {
        axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
        }).get('/employee/activate/status')
        .then((res) => {
        console.log(res.data.message)
        });
        },

        downloadModal: function () {
        this.eventHub.$emit('FilterOptions', { status: true });
        },

        getMyPermission: function () {
        //console.log(this.userData.id)
        axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
        }).get(`/admin/role/access/${this.userData.id}`)
        .then((res) => {
        var access = res.data.userAccess.moduleAction_id.split(",").map(Number)

        var dlResult = access.includes(this.canDownloadValue);
        if (dlResult) {
        this.canDownload = true
        } else {
        this.canDownload = false
        }

        var imResult = access.includes(this.canImportValue);
        if (imResult) {
        this.canImport = true
        } else {
        this.canImport = false
        }

        var exResult = access.includes(this.canEmportValue);
        if (exResult) {
        this.canExport = true
        } else {
        this.canExport = false
        }

        var edResult = access.includes(this.canEditValue);
        if (edResult) {
        this.canEdit = true
        } else {
        this.canEdit = false
        }

        var cuResult = access.includes(this.canUpdateCareerValue);
        if (cuResult) {
        this.canUpdateCareer = true
        } else {
        this.canUpdateCareer = false
        }

        var mbfResult = access.includes(this.canMBFValue);
        if (mbfResult) {
        this.canMBF = true
        } else {
        this.canMBF = false
        }

        var meritsResult = access.includes(this.canMBFValue);
        if (meritsResult) {
        this.canMerits = true
        } else {
        this.canMerits = false
        }

        });
        }
        }
        }
</script>
<style lang="css" scoped></style>
